import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Download,
  Filter,
  Store,
  RefreshCcw,
  Loader2,
  CheckCircle,
  Printer as PrintIcon,
  Tag,
  ArrowUpDown,
  Calendar,
  DollarSign,
  Receipt,
  PieChart,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { DataTable } from "@/components/ui/data-table";

import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";

export const ExpenseReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const [filters, setFilters] = useState(() => ({
    category_id: "",
    store_id: currentStore?.id ? String(currentStore.id) : "",
    employee_id: "",
    supplier_id: "",
    payment_method: "",
    status: "",
    start_date: "",
    end_date: "",
    min_amount: "",
    max_amount: "",
    sort_by: "date",
    sort_order: "desc",
  }));
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  const { data: categories = [], isFetching: isCategoriesFetching } = useQuery({
    enabled: hasPermission("report expense view"),
    queryKey: ["expense-categories-report"],
    queryFn: async () => {
      const response = await apiClient.get(`/expense-categories`);
      return response.data.data || response.data || [];
    },
  });

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report expense view"),
    queryKey: ["stores-report"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data || response.data || [];
    },
  });

  const { data: employees = [] } = useQuery({
    enabled: hasPermission("report expense view"),
    queryKey: ["employees-report"],
    queryFn: async () => {
      const response = await apiClient.get(`/employee`);
      return response.data.data || response.data || [];
    },
  });

  const { data: suppliers = [] } = useQuery({
    enabled: hasPermission("report expense view"),
    queryKey: ["suppliers-report"],
    queryFn: async () => {
      const response = await apiClient.get(`/supplier`);
      return response.data.data || response.data || [];
    },
  });

  const { data: availablePaymentMethods = [] } = useQuery({
    queryKey: ["payment-methods-available", "expenses", filters.store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "expenses",
          store_id: filters.store_id || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: hasPermission("payment-method read"),
    staleTime: 5 * 60 * 1000,
  });

  const buildParams = (currentFilters) => {
    const params = {};
    if (currentFilters.category_id)
      params.category_id = currentFilters.category_id;
    if (currentFilters.store_id) params.store_id = currentFilters.store_id;
    if (currentFilters.employee_id)
      params.employee_id = currentFilters.employee_id;
    if (currentFilters.supplier_id)
      params.supplier_id = currentFilters.supplier_id;
    if (
      currentFilters.payment_method &&
      currentFilters.payment_method !== "all"
    )
      params.payment_method = currentFilters.payment_method;
    if (currentFilters.status && currentFilters.status !== "all")
      params.status = currentFilters.status;
    if (currentFilters.start_date)
      params.start_date = currentFilters.start_date;
    if (currentFilters.end_date) params.end_date = currentFilters.end_date;
    if (currentFilters.min_amount)
      params.min_amount = currentFilters.min_amount;
    if (currentFilters.max_amount)
      params.max_amount = currentFilters.max_amount;
    if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
    if (currentFilters.sort_order)
      params.sort_order = currentFilters.sort_order;
    return params;
  };

  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report expense view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = buildParams(currentFilters);
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.EXPENSE,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report expense view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = buildParams(currentFilters);
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.EXPENSE,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Expense report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to generate expense report",
        "error",
      );
    },
  });

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      category_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      employee_id: "",
      supplier_id: "",
      payment_method: "",
      status: "",
      start_date: "",
      end_date: "",
      min_amount: "",
      max_amount: "",
      sort_by: "date",
      sort_order: "desc",
    });
  };

  const handleGenerateReport = () => {
    showToast("Generating expense report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `expense-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const categoryOptions = categories.map((category) => ({
    value: String(category.id),
    label: category.name || category.title || "Unknown Category",
  }));

  const storeOptions = [
    { value: "", label: "All Stores" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  const employeeOptions = employees.map((emp) => ({
    value: String(emp.id),
    label:
      emp.full_name ||
      `${emp.first_name || ""} ${emp.last_name || ""}`.trim() ||
      emp.name ||
      "Unknown",
  }));

  const supplierOptions = suppliers.map((sup) => ({
    value: String(sup.id),
    label: sup.company_name || sup.name || "Unknown Supplier",
  }));

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <FileText className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Financial Reporting
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Expense Report
              </span>
            </div>
          }
          subtitle="Generate detailed expense reports with advanced filtering for audits and financial analysis."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            className="rounded-xl mr-2"
            onClick={handleClearFilters}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report expense view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report expense view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-8">
        {/* Filter Panel */}
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Report Parameters</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Limit results by dates, amounts, people, and locations
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>

          {showFilters && (
            <CardContent className="p-8">
              {/* Date Filters */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2 mb-4">
                  <Calendar className="h-4 w-4 text-primary" /> Date Range
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      value={filters.start_date}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          start_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      value={filters.end_date}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, end_date: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Amount Filters */}
              <div className="mb-8">
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2 mb-4">
                  <DollarSign className="h-4 w-4 text-primary" /> Amount Range
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Min Amount</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0.00"
                      value={filters.min_amount}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          min_amount: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Max Amount</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="9999.00"
                      value={filters.max_amount}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          max_amount: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Entities */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Store className="h-4 w-4 text-primary" /> Location &
                    Entities
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Store</Label>
                      <ReactSelect
                        options={storeOptions}
                        value={
                          storeOptions.find(
                            (opt) => opt.value === String(filters.store_id || ""),
                          ) ||
                          (filters.store_id &&
                            currentStore?.id &&
                            String(filters.store_id) === String(currentStore.id)
                            ? {
                              value: String(currentStore.id),
                              label: currentStore.name,
                            }
                            : null)
                        }
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Stores"
                        isClearable={Boolean(filters.store_id)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Employee</Label>
                      <ReactSelect
                        options={employeeOptions}
                        value={employeeOptions.find(
                          (opt) => opt.value === String(filters.employee_id),
                        )}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            employee_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Employees"
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Supplier</Label>
                      <ReactSelect
                        options={supplierOptions}
                        value={supplierOptions.find(
                          (opt) => opt.value === String(filters.supplier_id),
                        )}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            supplier_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Suppliers"
                        isClearable
                      />
                    </div>
                  </div>
                </div>

                {/* Attributes */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Tag className="h-4 w-4 text-primary" /> Details & Status
                  </h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Category</Label>
                      <ReactSelect
                        options={categoryOptions}
                        isLoading={isCategoriesFetching}
                        value={categoryOptions.find(
                          (opt) => opt.value === String(filters.category_id),
                        )}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            category_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Categories"
                        isClearable
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Payment Method
                      </Label>
                      <Select
                        value={filters.payment_method}
                        onValueChange={(val) =>
                          setFilters((p) => ({
                            ...p,
                            payment_method: val === "all" ? "" : val,
                          }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="All Methods" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Methods</SelectItem>
                          {availablePaymentMethods.map((pm) => (
                            <SelectItem key={pm.id || pm.slug || pm} value={pm.id || pm.slug || pm}>
                              {pm.name || pm}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Status</Label>
                      <Select
                        value={filters.status}
                        onValueChange={(val) =>
                          setFilters((p) => ({
                            ...p,
                            status: val === "all" ? "" : val,
                          }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30 border-border">
                          <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="pending_approval">
                            Pending Approval
                          </SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t">
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2 mb-6">
                  <ArrowUpDown className="h-4 w-4 text-primary" /> Output
                  Sequencing
                </h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">
                      Sort Rows By
                    </Label>
                    <Select
                      value={filters.sort_by}
                      onValueChange={(val) =>
                        setFilters((p) => ({ ...p, sort_by: val }))
                      }
                    >
                      <SelectTrigger className="bg-muted/30 border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="amount">Amount</SelectItem>
                        <SelectItem value="total_amount">
                          Total Amount
                        </SelectItem>
                        <SelectItem value="category">Category</SelectItem>
                        <SelectItem value="title">Title</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">
                      Sorting Direction
                    </Label>
                    <Select
                      value={filters.sort_order}
                      onValueChange={(val) =>
                        setFilters((p) => ({ ...p, sort_order: val }))
                      }
                    >
                      <SelectTrigger className="bg-muted/30 border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {previewData.summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Grand Total
                      </p>
                      <h3 className="text-2xl font-bold text-primary">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.grand_total) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <DollarSign className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Amount (Excl. Tax)
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.total_amount) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-muted text-muted-foreground rounded-xl">
                      <Receipt className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Expenses
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {previewData.summary.total_expenses || 0}
                      </h3>
                    </div>
                    <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                      <FileText className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Average Expense
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.average_expense) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-success/10 text-success rounded-xl">
                      <PieChart className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Additional Breakdowns & Data */}
            {previewData.category_breakdown &&
              previewData.category_breakdown.length > 0 && (
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="shadow-none border border-border/50 overflow-hidden bg-background">
                    <CardHeader className="bg-muted/50 border-b pb-4 pt-4">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="h-5 w-5 text-primary" /> Category
                        Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="max-h-[480px] overflow-y-auto p-4 space-y-5">
                        {previewData.category_breakdown.map((item, idx) => (
                          <div key={idx} className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium text-foreground">
                                {item.category}
                              </span>
                              <span className="text-muted-foreground font-semibold">
                                {currencySymbol}
                                {(item.total || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full bg-primary rounded-full transition-all duration-500"
                                  style={{ width: `${item.percentage || 0}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground w-12 text-right">
                                {item.percentage}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="space-y-6">
                    {/* Status Breakdown */}
                    {previewData.summary?.by_status && (
                      <Card className="shadow-none border border-border/50 overflow-hidden bg-background">
                        <CardHeader className="bg-muted/50 border-b pb-4 pt-4">
                          <CardTitle className="text-lg flex items-center gap-2">
                            By Status
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 flex flex-col gap-3">
                          {Object.entries(previewData.summary.by_status).map(
                            ([status, data]) => (
                              <div
                                key={status}
                                className="flex items-center justify-between p-3 rounded-xl border bg-card hover:bg-muted/30 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-3 h-3 rounded-full ${status.toLowerCase() === "paid" ? "bg-success" : "bg-warning"}`}
                                  />
                                  <span className="font-medium capitalize text-foreground">
                                    {status.replace("_", " ")}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                                    {data.count} items
                                  </span>
                                  <span className="font-bold text-foreground">
                                    {currencySymbol}
                                    {(data.total || 0).toLocaleString(
                                      undefined,
                                      { minimumFractionDigits: 2 },
                                    )}
                                  </span>
                                </div>
                              </div>
                            ),
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Payment Method Breakdown */}
                    {previewData.summary?.by_payment_method && (
                      <Card className="shadow-none border border-border/50 overflow-hidden bg-background">
                        <CardHeader className="bg-muted/50 border-b pb-4 pt-4">
                          <CardTitle className="text-lg flex items-center gap-2">
                            By Payment Method
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 grid grid-cols-2 gap-3">
                          {Object.entries(
                            previewData.summary.by_payment_method,
                          ).map(([method, data]) => (
                            <div
                              key={method}
                              className="p-4 rounded-xl border bg-card space-y-2 flex flex-col justify-center"
                            >
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                {method.replace("_", " ")}
                              </p>
                              <p className="text-lg font-bold text-foreground">
                                {currencySymbol}
                                {(data.total || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                })}
                              </p>
                              <p className="text-[11px] text-muted-foreground">
                                {data.count} transaction(s)
                              </p>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Compiled!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Your expense report has been successfully generated and is
                  ready for download or printing.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};
